import { CountryCode } from '../../types';
import { COUNTRY_CODES } from '../config/countryCodes';

/**
 * 根据语言代码获取默认国家区号
 * 中文语言返回 +86（中国），其他语言返回 +1（美国）
 */
export const getDefaultCountryCode = (language: string): string => {
  const zhLanguages = ['zh', 'zh-cn', 'zh-tw', 'zh-hk'];
  const normalizedLang = language.toLowerCase();
  
  if (zhLanguages.some(lang => normalizedLang.startsWith(lang))) {
    return '+86';
  }
  return '+1';
};

/**
 * 根据语言代码获取默认国家数据
 */
export const getDefaultCountry = (language: string): CountryCode => {
  const defaultDialCode = getDefaultCountryCode(language);
  return COUNTRY_CODES.find(c => c.dialCode === defaultDialCode) || COUNTRY_CODES[0];
};

/**
 * 过滤国家列表
 * 支持按中文名、英文名、区号数字搜索（不区分大小写）
 */
export const filterCountries = (countries: CountryCode[], query: string): CountryCode[] => {
  if (!query || query.trim() === '') {
    return countries;
  }
  
  const normalizedQuery = query.toLowerCase().trim();
  
  return countries.filter(country => {
    // 匹配中文名称
    if (country.nameZh.includes(normalizedQuery)) {
      return true;
    }
    // 匹配英文名称（不区分大小写）
    if (country.nameEn.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    // 匹配区号（去掉+号后匹配）
    const dialCodeDigits = country.dialCode.replace('+', '');
    if (dialCodeDigits.includes(normalizedQuery.replace('+', ''))) {
      return true;
    }
    return false;
  });
};

/**
 * 格式化完整电话号码
 * 格式：{区号} {电话号码}
 */
export const formatPhoneNumber = (countryCode: string, phone: string): string => {
  const sanitizedPhone = sanitizePhoneNumber(phone);
  return `${countryCode} ${sanitizedPhone}`;
};

/**
 * 过滤电话号码，只保留数字
 */
export const sanitizePhoneNumber = (input: string): string => {
  return input.replace(/\D/g, '');
};

/**
 * 根据语言获取国家显示名称
 */
export const getCountryDisplayName = (country: CountryCode, language: string): string => {
  const zhLanguages = ['zh', 'zh-cn', 'zh-tw', 'zh-hk'];
  const normalizedLang = language.toLowerCase();
  
  if (zhLanguages.some(lang => normalizedLang.startsWith(lang))) {
    return country.nameZh;
  }
  return country.nameEn;
};

/**
 * 根据区号查找国家
 */
export const findCountryByDialCode = (dialCode: string): CountryCode | undefined => {
  return COUNTRY_CODES.find(c => c.dialCode === dialCode);
};
